import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  Badge, Button, Col, Container, FormGroup, Input, Label,
  Modal, ModalBody, ModalFooter, ModalHeader, Row,
} from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import ToggleSwitch from "../../shared/ToggleSwitch";
import { errorToast, successToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";
import { formatCurrency, formatPercent } from "../../utils/formatCurrency";
import {
  getWalletConfig, updateWalletConfig,
  getWalletPackages, createWalletPackage, updateWalletPackage, deleteWalletPackage,
} from "../../utils/apiCalls";


const EMPTY_PACKAGE = {
  name: "", arabicName: "", payAmount: "", creditAmount: "",
  sortOrder: 0, isActive: true, validFrom: "", validTo: "", maxPerCustomer: "",
};

const WalletSettings = () => {
  const userType = localStorage.getItem("userType");
  const loggedInMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  // The standard 4-line merchant-resolution idiom used across this panel.
  const selectedMerchantId = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enableWallet, setEnableWallet] = useState(false);
  const [merchantName, setMerchantName] = useState("");
  const [config, setConfig] = useState(null);
  const [packages, setPackages] = useState([]);

  const [pkgModal, setPkgModal] = useState(false);
  const [pkgForm, setPkgForm] = useState(EMPTY_PACKAGE);
  const [pkgEditingId, setPkgEditingId] = useState(null);
  const [pkgSaving, setPkgSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    if (!selectedMerchantId) return;
    setLoading(true);
    const [cfg, pkgs] = await Promise.all([
      getWalletConfig(selectedMerchantId),
      getWalletPackages(selectedMerchantId),
    ]);
    if (cfg) {
      setConfig(cfg.config);
      setEnableWallet(cfg.enableWallet);
      setMerchantName(cfg.merchantName || "");
    }
    setPackages(pkgs || []);
    setLoading(false);
  }, [selectedMerchantId]);

  // Reload on merchant change, and reset local state so a half-edited form from the previous
  // merchant can never be saved against the new one.
  useEffect(() => {
    setConfig(null);
    setPackages([]);
    setPkgModal(false);
    load();
  }, [load]);

  const setCfg = (key, value) => setConfig((c) => ({ ...c, [key]: value }));

  const saveConfig = async () => {
    setSaving(true);
    const result = await updateWalletConfig({ merchantId: selectedMerchantId, ...config });
    setSaving(false);
    if (result.ok) successToast("Wallet settings saved");
    else errorToast(result.message);
  };

  // ── packages

  const openCreate = () => {
    setPkgEditingId(null);
    setPkgForm(EMPTY_PACKAGE);
    setPkgModal(true);
  };

  const openEdit = (pkg) => {
    setPkgEditingId(pkg.id);
    setPkgForm({
      name: pkg.name ?? "",
      arabicName: pkg.arabicName ?? "",
      payAmount: pkg.payAmount ?? "",
      creditAmount: pkg.creditAmount ?? "",
      sortOrder: pkg.sortOrder ?? 0,
      isActive: pkg.isActive !== false,
      validFrom: pkg.validFrom ? String(pkg.validFrom).slice(0, 10) : "",
      validTo: pkg.validTo ? String(pkg.validTo).slice(0, 10) : "",
      maxPerCustomer: pkg.maxPerCustomer ?? "",
    });
    setPkgModal(true);
  };

  const pay = Number(pkgForm.payAmount) || 0;
  const credit = Number(pkgForm.creditAmount) || 0;
  const bonus = Math.round((credit - pay) * 100) / 100;
  const bonusPct = pay > 0 ? (bonus / pay) * 100 : 0;

  // Mirrors the server's rules so the operator is told before submitting. The server re-checks all
  // of it — this is a convenience, never the guarantee.
  const pkgErrors = [];
  if (!pkgForm.name.trim()) pkgErrors.push("Name is required.");
  if (pay <= 0) pkgErrors.push("Pay amount must be greater than zero.");
  if (credit <= 0) pkgErrors.push("Credit amount must be greater than zero.");
  if (credit > 0 && pay > 0 && credit < pay) pkgErrors.push("Credit cannot be less than the pay amount.");
  if (config?.minTopupAmount != null && pay > 0 && pay < Number(config.minTopupAmount))
    pkgErrors.push(`Pay amount is below this merchant's minimum top-up (${formatCurrency(config.minTopupAmount)}).`);
  if (config?.maxTopupAmount != null && pay > Number(config.maxTopupAmount))
    pkgErrors.push(`Pay amount is above this merchant's maximum top-up (${formatCurrency(config.maxTopupAmount)}).`);
  // Two live packages at the same price are indistinguishable to a customer.
  if (
    pay > 0 &&
    packages.some((p) => p.id !== pkgEditingId && p.isActive && Number(p.payAmount) === pay)
  ) pkgErrors.push("Another active package already charges this amount.");

  const savePackage = async () => {
    if (pkgErrors.length) return;
    setPkgSaving(true);
    const payload = {
      merchantId: selectedMerchantId,
      name: pkgForm.name.trim(),
      arabicName: pkgForm.arabicName.trim() || null,
      payAmount: pay,
      creditAmount: credit,
      sortOrder: Number(pkgForm.sortOrder) || 0,
      isActive: pkgForm.isActive,
      validFrom: pkgForm.validFrom || null,
      validTo: pkgForm.validTo || null,
      maxPerCustomer: pkgForm.maxPerCustomer === "" ? null : Number(pkgForm.maxPerCustomer),
      // bonusAmount deliberately omitted — the server derives it and ignores anything sent.
    };
    const result = pkgEditingId
      ? await updateWalletPackage(pkgEditingId, payload)
      : await createWalletPackage(payload);
    setPkgSaving(false);
    if (!result.ok) return errorToast(result.message);
    successToast(pkgEditingId ? "Package updated" : "Package created");
    setPkgModal(false);
    load();
  };

  const doDelete = async () => {
    const result = await deleteWalletPackage(confirmDelete.id, selectedMerchantId);
    setConfirmDelete(null);
    if (!result.ok) return errorToast(result.message);
    successToast("Package deactivated");
    load();
  };

  if (!selectedMerchantId) {
    return (
      <Fragment>
        <Breadcrumb title="Wallet Settings" parent="Customer Wallet" />
        <Container fluid>
          <style>{reportingGlobalCSS}</style>
          <div className="rpt-wrap">
            <RCard>
              <div style={{ padding: 32, textAlign: "center", color: C.textMed, fontSize: 13 }}>
                Please select a merchant to configure the customer wallet.
              </div>
            </RCard>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumb title="Wallet Settings" parent="Customer Wallet" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
        {/* Status banner. An operator who configures a wallet that is switched off would otherwise
            have no idea why nothing happens for customers. */}
        <RCard style={{ borderLeft: `4px solid ${enableWallet ? C.green : C.orange}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, color: C.text }}>
                Customer wallet is {enableWallet ? "ENABLED" : "DISABLED"}
                {merchantName ? ` for ${merchantName}` : ""}
              </div>
              <div style={{ fontSize: 13, color: C.textMed, marginTop: 4 }}>
                {enableWallet
                  ? "Customers can see and use their wallet in the app."
                  : "Settings below can be prepared now; nothing is visible to customers until the wallet is switched on."}
              </div>
            </div>
            <div style={{ fontSize: 12, color: C.textLight }}>
              Switch it on under <strong>Merchant → Edit Merchant</strong>
            </div>
          </div>
        </RCard>

        {loading || !config ? (
          <RCard><div style={{ padding: 32, textAlign: "center", color: C.textMed }}><RLoader /></div></RCard>
        ) : (
          <Row>
            <Col lg="6">
              <RCard>
                  <h5 style={{ color: C.text, marginBottom: 16 }}>Features</h5>
                  <ToggleRow label="Allow top-up" hint="Customers can buy credit with a card."
                    checked={config.allowTopup} onChange={(v) => setCfg("allowTopup", v)} />
                  <ToggleRow label="Allow transfers" hint="Customers can send purchased credit to another customer of this brand. Promotional credit is never transferable."
                    checked={config.allowTransfer} onChange={(v) => setCfg("allowTransfer", v)} />
                  <ToggleRow label="Allow paying with wallet" hint="Customers can pay for orders from their wallet balance. When the balance doesn't cover the total, the rest is charged to their card. Turning this off leaves top-ups and transfers working."
                    checked={config.allowPayment} onChange={(v) => setCfg("allowPayment", v)} />

                  <h5 style={{ color: C.text, margin: "24px 0 16px" }}>Limits</h5>
                  <Row>
                    <NumField col={6} label="Min top-up" hint="Also the floor on a customer-entered amount."
                      value={config.minTopupAmount} onChange={(v) => setCfg("minTopupAmount", v)} />
                    <NumField col={6} label="Max top-up" hint="Also the ceiling on a customer-entered amount."
                      value={config.maxTopupAmount} onChange={(v) => setCfg("maxTopupAmount", v)} />
                    <NumField col={6} label="Max balance" hint="Ceiling on a customer's total balance. Blank = no cap."
                      value={config.maxBalance} onChange={(v) => setCfg("maxBalance", v)} allowBlank />
                    <NumField col={6} label="Min transfer" value={config.minTransferAmount} onChange={(v) => setCfg("minTransferAmount", v)} />
                    <NumField col={6} label="Max transfer" value={config.maxTransferAmount} onChange={(v) => setCfg("maxTransferAmount", v)} />
                    <NumField col={6} label="Daily transfer total" hint="Blank = no cap."
                      value={config.dailyTransferCap} onChange={(v) => setCfg("dailyTransferCap", v)} allowBlank />
                    <NumField col={6} label="Daily transfer count" hint="Caps fan-out to many recipients."
                      value={config.dailyTransferCountCap} onChange={(v) => setCfg("dailyTransferCountCap", v)} allowBlank />
                  </Row>
              </RCard>
            </Col>

            <Col lg="6">
              <RCard>
                  <h5 style={{ color: C.text, marginBottom: 16 }}>Cancelled orders</h5>
                  <FormGroup>
                    <Label style={{ fontSize: 13, color: C.textMed }}>Default refund destination</Label>
                    <Input type="select" value={config.refundDestinationDefault || "original"}
                      onChange={(e) => setCfg("refundDestinationDefault", e.target.value)}>
                      <option value="original">Back to the original payment method</option>
                      <option value="wallet">To the customer's wallet</option>
                    </Input>
                    <div style={{ fontSize: 12, color: C.textLight, marginTop: 6 }}>
                      An admin can override this per cancellation. Note this only decides the
                      <strong> card-funded</strong> part of an order — anything paid from the wallet
                      always returns to the wallet.
                    </div>
                  </FormGroup>

                  <h5 style={{ color: C.text, margin: "24px 0 16px" }}>Terms shown in the app</h5>
                  <FormGroup>
                    <Label style={{ fontSize: 13, color: C.textMed }}>English</Label>
                    <Input type="textarea" rows={4} value={config.termsEn || ""}
                      onChange={(e) => setCfg("termsEn", e.target.value)} />
                  </FormGroup>
                  <FormGroup>
                    <Label style={{ fontSize: 13, color: C.textMed }}>Arabic</Label>
                    <Input type="textarea" rows={4} dir="rtl" value={config.termsAr || ""}
                      onChange={(e) => setCfg("termsAr", e.target.value)} />
                  </FormGroup>
              </RCard>
            </Col>

            <Col lg="12">
              <RCard>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div>
                      <h5 style={{ color: C.text, margin: 0 }}>Top-up packages</h5>
                      <div style={{ fontSize: 12, color: C.textLight, marginTop: 4 }}>
                        What a customer can buy. The bonus lands in promotional credit — spendable on
                        orders, never transferable and never refundable as cash.
                      </div>
                    </div>
                    <Button color="primary" onClick={openCreate} disabled={!config.allowTopup}>
                      Add package
                    </Button>
                  </div>

                  {!config.allowTopup && (
                    <div style={{ padding: 12, background: C.orangeLight, borderRadius: 6, fontSize: 13, color: C.text, marginBottom: 12 }}>
                      Top-up is switched off, so customers cannot buy any package.
                    </div>
                  )}

                  {packages.length === 0 ? (
                    <div style={{ padding: 24, textAlign: "center", color: C.textMed }}>
                      No packages yet. Customers can still add balance by entering their own
                      amount, within the Min and Max top-up limits. Packages exist to offer
                      bonus credit on top of what they pay.
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                      <table style={tbl.table}>
                        <thead>
                          <tr>
                            <th style={tbl.th}>Name</th>
                            <th style={{ ...tbl.th, textAlign: "right" }}>Pays</th>
                            <th style={{ ...tbl.th, textAlign: "right" }}>Gets</th>
                            <th style={tbl.th}>Bonus</th>
                            <th style={tbl.th}>Per customer</th>
                            <th style={tbl.th}>Window</th>
                            <th style={tbl.th}>Status</th>
                            <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {packages.map((p) => (
                            // Deactivated packages stay visible but dimmed: they may still be
                            // referenced by a historic top-up's packageSnapshot.
                            <tr key={p.id} className="rpt-row" style={{ opacity: p.isActive ? 1 : 0.55 }}>
                              <td style={tbl.td}>
                                <div style={{ fontWeight: 600, color: C.text }}>{p.name}</div>
                                {p.arabicName && <div style={{ color: C.textLight, fontSize: 12 }} dir="rtl">{p.arabicName}</div>}
                              </td>
                              <td style={{ ...tbl.td, textAlign: "right" }}>{formatCurrency(p.payAmount)}</td>
                              <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700 }}>{formatCurrency(p.creditAmount)}</td>
                              <td style={tbl.td}>
                                {p.bonusAmount > 0 ? (
                                  <Badge style={{ background: C.greenLight, color: C.green }}>
                                    +{formatCurrency(p.bonusAmount, { withCurrency: false })} ({formatPercent((p.bonusAmount / p.payAmount) * 100)})
                                  </Badge>
                                ) : <span style={{ color: C.textLight }}>—</span>}
                              </td>
                              <td style={{ ...tbl.td, color: C.textMed }}>
                                {p.maxPerCustomer ?? <span style={{ color: C.textLight }}>Unlimited</span>}
                              </td>
                              <td style={{ ...tbl.td, fontSize: 12, color: C.textMed }}>
                                {p.validFrom || p.validTo
                                  ? `${p.validFrom ? String(p.validFrom).slice(0, 10) : "—"} → ${p.validTo ? String(p.validTo).slice(0, 10) : "—"}`
                                  : "Always"}
                              </td>
                              <td style={tbl.td}>
                                <Badge style={p.isActive
                                  ? { background: C.greenLight, color: C.green }
                                  : { background: C.borderLight, color: C.textMed }}>
                                  {p.isActive ? "Active" : "Inactive"}
                                </Badge>
                              </td>
                              <td style={{ ...tbl.td, textAlign: "center", whiteSpace: "nowrap" }}>
                                <Button size="sm" color="primary" outline onClick={() => openEdit(p)}
                                  style={{ fontSize: 11, fontWeight: 700, borderRadius: 8, padding: "4px 12px" }}>
                                  Edit
                                </Button>
                                {p.isActive && (
                                  <Button size="sm" color="warning" outline
                                    style={{ fontSize: 11, fontWeight: 700, borderRadius: 8, padding: "4px 12px", marginLeft: 6 }}
                                    onClick={() => setConfirmDelete(p)}>
                                    Deactivate
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </RCard>
            </Col>

            <Col lg="12" style={{ marginBottom: 24 }}>
              <Button color="primary" onClick={saveConfig} disabled={saving}>
                {saving ? "Saving…" : "Save wallet settings"}
              </Button>
            </Col>
          </Row>
        )}

        {/* ── package modal */}
        <Modal isOpen={pkgModal} toggle={() => setPkgModal(false)} centered>
          <ModalHeader toggle={() => setPkgModal(false)}>
            {pkgEditingId ? "Edit package" : "New top-up package"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md="12">
                <FormGroup>
                  <Label style={{ fontSize: 13 }}>Name (English)</Label>
                  <Input value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label style={{ fontSize: 13 }}>Name (Arabic)</Label>
                  <Input dir="rtl" value={pkgForm.arabicName} onChange={(e) => setPkgForm({ ...pkgForm, arabicName: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label style={{ fontSize: 13 }}>Customer pays</Label>
                  <Input type="number" min="0" step="0.01" value={pkgForm.payAmount}
                    onChange={(e) => setPkgForm({ ...pkgForm, payAmount: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label style={{ fontSize: 13 }}>Customer gets</Label>
                  <Input type="number" min="0" step="0.01" value={pkgForm.creditAmount}
                    onChange={(e) => setPkgForm({ ...pkgForm, creditAmount: e.target.value })} />
                </FormGroup>
              </Col>
            </Row>

            {/* Bonus is COMPUTED and shown, never entered — the server derives it and ignores any
                value sent, so an editable field here could only ever disagree with reality. */}
            <div style={{ background: bonus > 0 ? C.greenLight : C.borderLight, borderRadius: 6, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: C.textMed }}>Bonus (calculated)</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: bonus > 0 ? C.green : C.textMed }}>
                {formatCurrency(bonus)} {bonus > 0 && <span style={{ fontSize: 14 }}>({formatPercent(bonusPct)} extra)</span>}
              </div>
            </div>

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label style={{ fontSize: 13 }}>Max per customer</Label>
                  <Input type="number" min="1" placeholder="Unlimited" value={pkgForm.maxPerCustomer}
                    onChange={(e) => setPkgForm({ ...pkgForm, maxPerCustomer: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label style={{ fontSize: 13 }}>Sort order</Label>
                  <Input type="number" value={pkgForm.sortOrder}
                    onChange={(e) => setPkgForm({ ...pkgForm, sortOrder: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label style={{ fontSize: 13 }}>Valid from</Label>
                  <Input type="date" value={pkgForm.validFrom}
                    onChange={(e) => setPkgForm({ ...pkgForm, validFrom: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label style={{ fontSize: 13 }}>Valid to</Label>
                  <Input type="date" value={pkgForm.validTo}
                    onChange={(e) => setPkgForm({ ...pkgForm, validTo: e.target.value })} />
                </FormGroup>
              </Col>
            </Row>

            <ToggleRow label="Active" hint="Inactive packages are hidden from customers."
              checked={pkgForm.isActive} onChange={(v) => setPkgForm({ ...pkgForm, isActive: v })} />

            {/* Customer-facing preview, so an operator sees the real copy before publishing. */}
            {pay > 0 && credit > 0 && (
              <div style={{ marginTop: 12, padding: 12, border: `1px dashed ${C.border}`, borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Customer sees
                </div>
                <div style={{ marginTop: 6, fontSize: 15, color: C.text }}>
                  <strong>{pkgForm.name || "Package"}</strong> — pay {formatCurrency(pay)}, get{" "}
                  <strong>{formatCurrency(credit)}</strong>
                  {bonus > 0 && <span style={{ color: C.green }}> · {formatPercent(bonusPct)} extra</span>}
                </div>
              </div>
            )}

            {pkgErrors.length > 0 && (
              <div style={{ marginTop: 12, padding: 10, background: "#FEF2F2", borderRadius: 6 }}>
                {pkgErrors.map((e) => (
                  <div key={e} style={{ fontSize: 12, color: "#B91C1C" }}>• {e}</div>
                ))}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setPkgModal(false)}>Cancel</Button>
            <Button color="primary" onClick={savePackage} disabled={pkgSaving || pkgErrors.length > 0}>
              {pkgSaving ? "Saving…" : pkgEditingId ? "Save changes" : "Create package"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* A real modal rather than window.confirm: the message needs to explain that this is a
            deactivation, not a deletion, and that existing purchases are unaffected. */}
        <Modal isOpen={!!confirmDelete} toggle={() => setConfirmDelete(null)} centered>
          <ModalHeader toggle={() => setConfirmDelete(null)}>Deactivate package</ModalHeader>
          <ModalBody>
            <p style={{ color: C.text }}>
              Deactivate <strong>{confirmDelete?.name}</strong>?
            </p>
            <p style={{ fontSize: 13, color: C.textMed, marginBottom: 0 }}>
              It disappears from the app immediately. Top-ups already bought with it keep their
              record and are not affected — nothing is deleted.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button color="warning" onClick={doDelete}>Deactivate</Button>
          </ModalFooter>
        </Modal>
        </div>
      </Container>
    </Fragment>
  );
};

const ToggleRow = ({ label, hint, checked, onChange }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 0", borderBottom: `1px solid ${C.borderLight}` }}>
    <div style={{ paddingRight: 16 }}>
      <div style={{ fontSize: 14, color: C.text }}>{label}</div>
      {hint && <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>{hint}</div>}
    </div>
    {/* ToggleSwitch's prop is `handleToggle`, and react-switch hands it the new boolean directly
        (not a DOM event) — passing `onChange` here would silently render a switch that never fires. */}
    <ToggleSwitch checked={!!checked} handleToggle={(next) => onChange(next)} />
  </div>
);

const NumField = ({ col = 6, label, hint, value, onChange, allowBlank = false }) => (
  <Col md={String(col)}>
    <FormGroup>
      <Label style={{ fontSize: 13, color: C.textMed }}>{label}</Label>
      <Input
        type="number" min="0" step="0.01"
        value={value ?? ""}
        placeholder={allowBlank ? "No limit" : ""}
        onChange={(e) => onChange(e.target.value === "" && allowBlank ? null : e.target.value)}
      />
      {hint && <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>{hint}</div>}
    </FormGroup>
  </Col>
);

export default WalletSettings;
